import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";

export type ShoeType = "race_carbon" | "tempo" | "daily" | "max_cushion" | "trail" | "recovery";

export type ShoeModel = {
  id: number;
  slug: string;
  brand: string;
  name: string;
  year: number;
  weight_g: number;
  drop_mm: number;
  stack_heel_mm: number | null;
  stack_toe_mm: number | null;
  shoe_type: ShoeType;
  foam_firmness: "soft" | "medium" | "firm";
  carbon_plate: boolean;
  lifespan_km_default: number;
  price_eur: number | null;
  image_url: string | null;
  official_url: string | null;
  is_available: boolean;
};

export type AthleteShoe = {
  id: number;
  shoe_model: ShoeModel;
  nickname: string | null;
  purchased_date: string;
  starting_km: number;
  current_km: number;
  lifespan_km: number;
  freshness_pct: number;
  is_primary: boolean;
  retired_at: string | null;
  notes: string | null;
  last_synced_at: string | null;
  /** Привязка к снаряжению источника. Есть — пробег измерен, нет — распределён. */
  intervals_gear_id: string | null;
  km_is_measured: boolean;
};

/**
 * Снаряжение, найденное в истории тренировок.
 *
 * Идентификатор приходил от источника с самого начала и выбрасывался
 * при импорте. Теперь по нему видно, сколько пар было в ротации и
 * сколько километров каждая прошла НА САМОМ ДЕЛЕ — пара заводится
 * отсюда, а не с нуля.
 *
 * Имени нет: источник шлёт идентификатор и пустое имя. Опознаётся пара
 * по периоду и пробегу, название человек даёт сам.
 */
export type DiscoveredGear = {
  gear_external_id: string;
  discipline: string;
  workouts: number;
  distance_km: number;
  first_seen: string;
  last_seen: string;
  already_linked: boolean;
};

export type Recommendation = {
  model: ShoeModel;
  score: number;
  reasons: string[];
};

export type CatalogFilters = {
  shoe_type?: ShoeType;
  brand?: string;
  carbon?: boolean;
  max_weight_g?: number;
};

export function useShoeCatalog(filters: CatalogFilters = {}) {
  return useQuery<{ items: ShoeModel[] }>({
    queryKey: ["shoe-catalog", filters],
    queryFn: async () => {
      const params: Record<string, string | number | boolean> = {};
      if (filters.shoe_type) params.shoe_type = filters.shoe_type;
      if (filters.brand) params.brand = filters.brand;
      if (filters.carbon !== undefined) params.carbon = filters.carbon;
      if (filters.max_weight_g) params.max_weight_g = filters.max_weight_g;
      const r = await api.get<{ items: ShoeModel[] }>("/athlete/shoes/catalog", { params });
      return r.data;
    },
    staleTime: 5 * 60_000,
  });
}

export function useShoeRecommendations(kind?: ShoeType, target_distance_km?: number) {
  return useQuery<{ items: Recommendation[] }>({
    queryKey: ["shoe-recommendations", kind, target_distance_km],
    queryFn: async () => {
      const params: Record<string, string | number> = {};
      if (kind) params.kind = kind;
      if (target_distance_km) params.target_distance_km = target_distance_km;
      const r = await api.get<{ items: Recommendation[] }>(
        "/athlete/shoes/recommendations",
        { params },
      );
      return r.data;
    },
    staleTime: 5 * 60_000,
  });
}

export function useMyShoes(refresh = false) {
  return useQuery<{ items: AthleteShoe[] }>({
    queryKey: ["athlete-shoes", refresh],
    queryFn: async () => {
      const r = await api.get<{ items: AthleteShoe[] }>(
        `/athlete/shoes?refresh=${refresh}`,
      );
      return r.data;
    },
    staleTime: 30_000,
  });
}

export interface ShoeEconomyRow {
  shoe_id: number;
  label: string;
  runs: number;
  total_km: number;
  /** Метров в минуту на удар пульса, приведённое к 15 °C. */
  economy: number;
  /** Разброс между пробежками — без него среднее выглядит точнее, чем есть. */
  spread: number;
  avg_temp_c: number | null;
  /** Пробежки, которые не удалось привести к опорной температуре. */
  unadjusted: number;
}

export interface ShoeEconomy {
  shoes: ShoeEconomyRow[];
  /** Пары, где пробежек меньше порога сравнения. */
  too_few: [string, number][];
}

export function useShoeEconomy(days = 365) {
  return useQuery<ShoeEconomy>({
    queryKey: ["athlete-shoe-economy", days],
    queryFn: async () => {
      const r = await api.get<ShoeEconomy>(`/athlete/shoes/economy?days=${days}`);
      return r.data;
    },
    staleTime: 10 * 60_000,
  });
}

export function useAddShoe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      shoe_model_id: number;
      purchased_date: string;
      nickname?: string;
      starting_km?: number;
      is_primary?: boolean;
      lifespan_km_override?: number;
      notes?: string;
      /** Из «найдено в истории»: пара заводится сразу с настоящим пробегом. */
      intervals_gear_id?: string;
    }) => {
      const r = await api.post<AthleteShoe>("/athlete/shoes", body);
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["athlete-shoes"] });
      // Список найденного помечает уже привязанное — он тоже устарел.
      qc.invalidateQueries({ queryKey: ["shoes-discovered"] });
    },
  });
}

export function usePatchShoe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      id: number;
      patch: {
        nickname?: string;
        is_primary?: boolean;
        retired?: boolean;
        lifespan_km_override?: number;
        notes?: string;
      };
    }) => {
      const r = await api.patch<AthleteShoe>(`/athlete/shoes/${vars.id}`, vars.patch);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["athlete-shoes"] }),
  });
}

export function useDeleteShoe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/athlete/shoes/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["athlete-shoes"] }),
  });
}

export const SHOE_TYPE_LABEL: Record<ShoeType, string> = {
  race_carbon: "Гонки carbon",
  tempo: "Темпо",
  daily: "Daily",
  max_cushion: "Длинные с макс. амортизацией",
  trail: "Трейл",
  recovery: "Восстановление",
};

export function freshnessTone(pct: number): "good" | "ok" | "warn" | "bad" {
  if (pct >= 70) return "good";
  if (pct >= 50) return "ok";
  if (pct >= 30) return "warn";
  return "bad";
}


export function useDiscoveredGear() {
  return useQuery<{ items: DiscoveredGear[] }>({
    queryKey: ["shoes-discovered"],
    queryFn: async () => {
      const r = await api.get<{ items: DiscoveredGear[] }>("/athlete/shoes/discovered");
      return r.data;
    },
    staleTime: 10 * 60_000,
  });
}
